import { Component, OnDestroy, OnInit } from '@angular/core';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { SliderTypes } from '../../shared/slider/slider.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import {
  CompanyLookupResponseModel,
  CompanySearchResponseModel,
} from 'src/app/models/company.model';

import { CompanyApiService } from 'src/app/services/api/company-api.service';
import { TranslateService } from '@ngx-translate/core';
import { phoneNumberValidator } from 'src/app/validators/phoneNumber';
import { debounceTime, tap } from 'rxjs/operators';
import { LeadApiService } from 'src/app/services/api/lead-api.service';
import {
  CreateLeadResponseModel,
  UpdateLeadResponseModel,
} from 'src/app/models/lead.model';
import { handleApiError } from 'src/app/utils/error';
import { reasonOptions } from 'src/configs/dropdown-options/options.se';
import { PersonalInfoService } from 'src/app/services/personal-info.service';
import { Subscription } from 'rxjs';
import { TrackingService } from 'src/app/services/tracking.service';
import { FlowService } from 'src/app/services/flow.service';
import { Flows } from 'src/app/app.consts';
import { parseLocaleNumber } from 'src/app/utils/format-currency';
import { LocaleService } from 'src/app/services/locale.service';
import { environment } from 'src/environments/local/environment';
import { StorageService } from 'src/app/services/storage.service';
import { ActivatedRoute } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '../../shared/dialog/dialog.component';
import { Overlay } from '@angular/cdk/overlay';

declare type FormValuesType = {
  id: string;
  amount: number;
  duration: number;
  email: string;
  phoneNumber: string;
  clientId: string;
  reason: string;
  agreedToTerms: boolean;
};

@Component({
  selector: 'app-basic-info-screen',
  templateUrl: './basic-info-screen-nl.component.html',
  styleUrls: ['./basic-info-screen.component.scss'],
})
export class BasicInfoScreenComponent implements OnInit, OnDestroy {
  sliderTypes = SliderTypes;
  error = '';
  basicInfoForm!: FormGroup;
  companies!: Array<CompanySearchResponseModel>;
  leadCreated = false;
  leadId = '';
  reasonOptions = reasonOptions;
  subs = Array<Subscription>();
  loading = false;
  searchSelected = false;
  tosLink = environment.tos;
  confirmationLoading = false;

  constructor(
    protected basicInfoService: BasicInfoService,
    protected formBuilder: FormBuilder,
    protected companyApi: CompanyApiService,
    protected leadApi: LeadApiService,
    protected translateService: TranslateService,
    protected personalInfoService: PersonalInfoService,
    protected trackingService: TrackingService,
    protected localeService: LocaleService,
    protected storageService: StorageService,
    protected route: ActivatedRoute,
    public flowService: FlowService,
    protected dialog: MatDialog,
    protected overlay: Overlay
  ) {}

  ngOnInit(): void {
    this.flowService.selectFlow(Flows.INITIAL);

    this.basicInfoService.fetchQueryParams();
    const initialCashAmount = this.basicInfoService.amount || 100000;
    const initialDuration = this.basicInfoService.duration || 12;
    const agreedToTerms = this.basicInfoService.agreedToTerms;

    this.basicInfoForm = this.formBuilder.group({
      amount: [initialCashAmount],
      duration: [initialDuration],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, phoneNumberValidator()]],
      companyName: ['', [Validators.required]],
      reason: [null, [Validators.required]],
      agreedToTerms: [agreedToTerms],
    });

    this.subs.push(
      this.companyName.valueChanges.pipe(debounceTime(1000)).subscribe((v) => {
        if (v.length > 2) {
          this.loading = true;
          this.companyApi.companySearch(v).subscribe(
            (response: CompanySearchResponseModel[]) => {
              this.loading = false;
              this.companies = response;
            },
            (error: any) => {
              this.error = handleApiError(error, this.translateService);
              this.loading = false;
            }
          );
          if (v === 'cannotFindCompany') {
            this.companies = [];
            this.companyName.patchValue('');
          }
        }
      })
    );

    this.subs.push(
      this.form.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
        if (!this.leadCreated) {
          if (this.email.valid || this.phoneNumber.valid) {
            const formValues = this.getFormValues();
            this.leadApi
              .createLead(this.trackingService.populateTrackingData(formValues))
              .subscribe(
                (response: CreateLeadResponseModel) => {
                  if (response.id) {
                    this.leadCreated = true;
                    this.leadId = response.id;
                  }
                  this.trackFields();
                },
                (error: any) => {
                  this.error = handleApiError(error, this.translateService);
                }
              );
          }
        } else {
          this.updateLead();
        }
      })
    );

    this.subs.push(
      this.route.queryParams.subscribe((params) => {
        const { max_amount } = params;
        if (max_amount) {
          const maxAmount = parseInt(max_amount, 10);
          this.amount.patchValue(Math.round(maxAmount / 500) * 500 * 0.5);
        }
        // If utm_source is attached, they have come through directly to loan
        if (params.utm_source) {
          this.trackingService.populateTackingDirectVisit(params);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }

  getFormValues(id?: string): FormValuesType {
    const values: { [key: string]: string | number | boolean } = {
      id: id ? id : '',
    };

    Object.keys(this.form.controls).map((key) => {
      const control = this.form.get(key) as FormControl;
      if (control.valid) {
        if (key === 'companyName' && typeof control.value === 'object') {
          values[key] = control.value.companyName;
        } else if (key === 'amount' && typeof control.value === 'string') {
          values[key] = parseLocaleNumber(
            control.value,
            this.localeService.locale
          );
        } else {
          values[key] = control.value;
        }
      }
    });

    if (values.phoneNumber && typeof values.phoneNumber === 'string') {
      values.phoneNumber = values.phoneNumber
        .replace(/\s/g, '')
        .replace(/[+]/g, '')
        .replace(/-/g, '');
    }

    return values as FormValuesType;
  }

  trackFields(): void {
    if (this.email.valid) {
      this.trackingService.filledField('email');
    }
    if (this.phoneNumber.valid) {
      this.trackingService.filledField('phoneNumber');
    }
    if (this.reason.valid) {
      this.trackingService.filledField('reason');
    }
    if (this.companyName.valid) {
      this.trackingService.filledField('company');
    }
  }

  get form(): FormGroup {
    return this.basicInfoForm;
  }

  get amount(): FormControl {
    return this.form.get('amount') as FormControl;
  }

  get duration(): FormControl {
    return this.form.get('duration') as FormControl;
  }

  get email(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get phoneNumber(): FormControl {
    return this.form.get('phoneNumber') as FormControl;
  }

  get companyName(): FormControl {
    return this.form.get('companyName') as FormControl;
  }

  get reason(): FormControl {
    return this.form.get('reason') as FormControl;
  }

  get agreedToTerms(): FormControl {
    return this.form.get('agreedToTerms') as FormControl;
  }

  handleButtonClick(): void {
    if (this.leadCreated && this.leadId) {
      this.agreedToTerms.setValue(true);
      this.updateLead();
    }
    if (this.form.invalid || this.confirmationLoading) {
      return;
    }
    if (!this.searchSelected) {
      this.companyName.setErrors({ companySearchRequired: true });
      return;
    }
    this.confirmationLoading = true;

    this.subs.push(
      this.companyApi
        .companyLookup(this.companyName.value.companyId)
        .pipe(
          tap((companyData) => {
            if (this.handleIsCompanyNull(companyData)) {
              this.companyName.setErrors({ companyIsNull: true });
              throw new Error(
                this.translateService.instant(
                  'global.error.issueFetchingCompanyData.thrownErrorText'
                )
              );
            }
          })
        )
        .subscribe(
          (resp: CompanyLookupResponseModel) => {
            this.populateBasicInfoService(resp);
            this.personalInfoService.email = this.email.value;
            this.storageService.setLeadId(this.leadId);
            this.storageService.setCompany(resp);
            this.flowService.nextScreen();
          },
          (error: any) => {
            if (
              error.message ===
              this.translateService.instant(
                'global.error.issueFetchingCompanyData.thrownErrorText'
              )
            ) {
              this.openDialog();
            }
            this.error = handleApiError(error, this.translateService);
            this.confirmationLoading = false;
          }
        )
    );
  }

  updateLead(): void {
    this.subs.push(
      this.leadApi.updateLead(this.getFormValues(this.leadId)).subscribe(
        (response: UpdateLeadResponseModel) => {
          if (response.id) {
            this.error = '';
          }
          this.trackFields();
        },
        (error: any) => {
          this.error = handleApiError(error, this.translateService);
        }
      )
    );
  }

  populateBasicInfoService(lookupData: CompanyLookupResponseModel): void {
    const formValues = this.getFormValues();
    this.basicInfoService.amount = formValues.amount;
    this.basicInfoService.duration = formValues.duration;
    this.basicInfoService.email = formValues.email;
    this.basicInfoService.phoneNumber = formValues.phoneNumber;
    this.basicInfoService.company = lookupData;
    this.basicInfoService.reason = formValues.reason;
    this.basicInfoService.leadId = this.leadId;
  }

  handleSearchSelected(value: boolean): void {
    if (value) {
      this.companyName.setErrors(null);
    }
    this.searchSelected = value;
  }

  handleIsCompanyNull(companyData: CompanyLookupResponseModel): boolean {
    this.companyName.setErrors(null);
    if (Object.keys(companyData).length === 0) return true;
    return false;
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(DialogComponent, {
      maxWidth: '400px',
      data: {
        title: this.translateService.instant(
          'global.error.issueFetchingCompanyData.title'
        ),
        question: this.translateService.instant(
          'global.error.issueFetchingCompanyData.content'
        ),
        yes: this.translateService.instant(
          'global.error.issueFetchingCompanyData.buttonText'
        ),
        align: 'end',
        backgroundColor: '#f2f2f2',
        icon: 'icon-frown',
      },
      autoFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe();
  }

  getErrors(control: string): string | null {
    const amount = this.amount;
    const email = this.email;
    const phoneNumber = this.phoneNumber;
    const companyName = this.companyName;
    const reason = this.reason;

    switch (control) {
      case 'amount':
        if (amount.hasError('invalidCurrencyMinimum')) {
          return this.translateService.instant(
            'global.error.invalidCurrencyMinimum'
          );
        }
        return null;
      case 'email':
        if (email.hasError('email') && !email.hasError('required')) {
          return this.translateService.instant('global.error.emailNotValid');
        } else if (email.hasError('required')) {
          return this.translateService.instant('global.error.emailRequired');
        }
        return null;
      case 'phoneNumber':
        if (phoneNumber.hasError('required')) {
          return this.translateService.instant(
            'global.error.phoneNumberRequired'
          );
        } else if (
          (!phoneNumber.hasError('required') &&
            phoneNumber.hasError('invalidPhoneNumber')) ||
          phoneNumber.invalid
        ) {
          return this.translateService.instant(
            'global.error.phoneNumberNotValid'
          );
        }
        return null;
      case 'companyName':
        if (companyName.hasError('required')) {
          return this.translateService.instant(
            'global.error.companyNameOrIdRequired'
          );
        } else if (companyName.hasError('companySearchRequired')) {
          return this.translateService.instant(
            'basicInfo.companySearchRequired'
          );
        } else if (companyName.hasError('companyIsNull')) {
          return this.translateService.instant(
            'global.error.issueFetchingCompanyData.companyIsNull'
          );
        }
        return null;
      case 'reason':
        if (reason.hasError('required')) {
          return this.translateService.instant('global.error.reasonRequired');
        }
        return null;
      default:
        return null;
    }
  }
}
